import { PointCloudObject } from "@/object-cache";
import { useAppSelector } from "@/store/store-hooks";
import {
  CachedWorldTransform,
  selectIElementWorldTransform,
} from "@faro-lotv/project-source";
import { useMemo } from "react";
import { Box3, Matrix4, Quaternion, Vector3 } from "three";

type CenterOnCloudResult = {
  /** Real transform used to render the point cloud */
  cloudTransform: CachedWorldTransform;
  /** The cloud bounding box */
  box: Box3;
  /** Point the camera is looking at */
  target: Vector3;
  /** Target camera position to look at the cloud */
  targetCameraPos: Vector3;
};

/**
 * Hook used to center the camera on a pointCloud.
 *
 * @param pointCloudObject The point cloud
 * @returns the target for the camera
 */
export function useCenterCameraOnPointCloud(
  pointCloudObject: PointCloudObject,
): CenterOnCloudResult {
  const modelTransform = useAppSelector(
    selectIElementWorldTransform(pointCloudObject.iElement.id),
  );

  const cloudTransform = useMemo<CachedWorldTransform>(() => {
    const p = new Vector3().fromArray(modelTransform.position);
    const q = new Quaternion().fromArray(modelTransform.quaternion);
    const m = new Matrix4().compose(p, q, new Vector3(1, 1, 1));
    return {
      position: p.toArray(),
      quaternion: [q.x, q.y, q.z, q.w],
      scale: [1, 1, 1],
      worldMatrixMixedHd: m.toArray(),
      worldMatrix: m.clone().toArray(),
    };
  }, [modelTransform]);

  const box = useMemo(() => {
    const b = pointCloudObject.tree.boundingBox.clone();
    b.applyMatrix4(new Matrix4().fromArray(cloudTransform.worldMatrix));
    return b;
  }, [pointCloudObject, cloudTransform]);

  // Position of where the camera needs to look at
  const target = useMemo(() => box.getCenter(new Vector3()), [box]);

  const targetCameraPos = useMemo(() => {
    const delta = new Vector3().subVectors(box.max, box.min);
    const t = delta.x;
    delta.x = delta.z;
    delta.z = t;
    return new Vector3().addVectors(target, delta);
  }, [target, box]);

  return {
    cloudTransform,
    box,
    target,
    targetCameraPos,
  };
}
